import { OrganizationRaw } from "@/domain/Organization";
import { Sheet } from "@/domain/Sheet";
import { TadTimeRange, encodeTadTimeRange } from "@/domain/TadTimeRange";
import { Version } from "@/domain/Version";
import { API_CLIENT } from "../config";
import { postDownloadFileFromAPI } from "../helpers";
import {
  PostTadSheetDataUploadResponse,
  TadSheetDataDownloadResult,
} from "../tadSheets";
import { countDownloadedRows } from "../tadSheets/downloadedRows";

/**
 * TADS参照シートを経由して、参照先 TADS の行を読み書きする API。
 *
 * 対象は参照シートの座標 (org_id, version, sheet_id) で指定し、参照先 TADS の ID は
 * api-server がシートの `tad_refer_to` から解決する。`timeRange` は TADS参照が持つ
 * 時間範囲の内側でなければならず (`limit` も参照の値以下)、逸脱すると 400 になる。
 * 書き込みは参照シートと参照先 TADS の両方への書き込み権限が、読み取りは参照シートへの
 * 読み取り権限のみが必要。
 */
const apiUrlTadSheetReferencesData = "/files/tad_sheet_references/data";

type TadSheetReferenceTarget = {
  organizationId: OrganizationRaw["org_id"];
  versionId: Version["id"];
  sheetId: Sheet["id"];
};

const toParams = ({
  organizationId,
  versionId,
  sheetId,
}: TadSheetReferenceTarget) => ({
  org_id: organizationId,
  version: versionId,
  sheet_id: sheetId,
});

const postTadSheetReferenceData = async ({
  timeRange,
  file,
  endpoint,
  ...target
}: TadSheetReferenceTarget & {
  timeRange: TadTimeRange;
  file: File;
  endpoint: string;
}): Promise<PostTadSheetDataUploadResponse> => {
  const form = new FormData();
  form.append("file", file);
  form.append("time_range", JSON.stringify(encodeTadTimeRange(timeRange)));
  const response = await API_CLIENT.post<{
    tad_sheet_id: string;
    imported_rows: number;
  }>(endpoint, form, { params: toParams(target) });
  return {
    tadSheetId: response.data.tad_sheet_id,
    importedRows: response.data.imported_rows,
  };
};

/** 参照先 TADS に行を追加する (追記)。行の時間軸カラムは `timeRange` の内側である必要がある */
export const postUploadTadSheetReferenceDataCSV = (
  params: TadSheetReferenceTarget & { timeRange: TadTimeRange; file: File }
): Promise<PostTadSheetDataUploadResponse> =>
  postTadSheetReferenceData({
    ...params,
    endpoint: `${apiUrlTadSheetReferencesData}/csv`,
  });

/** 参照先 TADS に行を追加する (追記)。行の時間軸カラムは `timeRange` の内側である必要がある */
export const postUploadTadSheetReferenceDataJSON = (
  params: TadSheetReferenceTarget & { timeRange: TadTimeRange; file: File }
): Promise<PostTadSheetDataUploadResponse> =>
  postTadSheetReferenceData({
    ...params,
    endpoint: `${apiUrlTadSheetReferencesData}/json`,
  });

/** 参照先 TADS の `timeRange` にある行をすべて削除し、ファイルの内容に置き換える (範囲置換) */
export const postReplaceTadSheetReferenceDataCSV = (
  params: TadSheetReferenceTarget & { timeRange: TadTimeRange; file: File }
): Promise<PostTadSheetDataUploadResponse> =>
  postTadSheetReferenceData({
    ...params,
    endpoint: `${apiUrlTadSheetReferencesData}/csv/replace`,
  });

/** 参照先 TADS の `timeRange` にある行をすべて削除し、ファイルの内容に置き換える (範囲置換) */
export const postReplaceTadSheetReferenceDataJSON = (
  params: TadSheetReferenceTarget & { timeRange: TadTimeRange; file: File }
): Promise<PostTadSheetDataUploadResponse> =>
  postTadSheetReferenceData({
    ...params,
    endpoint: `${apiUrlTadSheetReferencesData}/json/replace`,
  });

/**
 * 参照先 TADS の `timeRange` にある行を JSON でダウンロードする。
 * TADS参照経由では CSV のダウンロードは提供されていない。
 */
export const postDownloadTadSheetReferenceDataJSON = async ({
  timeRange,
  fileName,
  ...target
}: TadSheetReferenceTarget & {
  timeRange: TadTimeRange;
  /** 拡張子なしのファイル名。日時は `downloadBlobAsFile` が付ける */
  fileName: string;
}): Promise<TadSheetDataDownloadResult> => {
  const response = await postDownloadFileFromAPI(
    `${apiUrlTadSheetReferencesData}/json/download`,
    { time_range: encodeTadTimeRange(timeRange) },
    fileName,
    "json",
    { params: toParams(target) }
  );
  // ファイルの保存は済んでいるので、行数を数えられなくても失敗にはしない
  let rowCount: number | null = null;
  try {
    rowCount = countDownloadedRows(await response.data.text(), "json");
  } catch {
    rowCount = null;
  }
  return {
    rowCount,
    isPossiblyTruncated: rowCount !== null && rowCount >= timeRange.limit,
  };
};
